import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import Stack, { StackProps as MuiStackProps } from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import {
  Appointment,
  ArrowLeftIcon,
  ChatIcon,
  CycleIcon,
  LikeIcon,
  LinkIcon,
  PlayIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { CircularProgress } from '@classity-edtech/ui-base';

export interface ProductsProps extends MuiStackProps {
  lessons: {
    month: string;
    lessons: {
      appointmentDay: string;
      appointmentDate: string;
      appointmentTime: string;
      appointmentLocation: string;
      appointmentInfo: string;
      url: string;
      isDisabled: boolean;
      clickAction?: () => void;
    }[];
  }[];
  recordings: any[];
  isLoading: boolean;
}

export const Products = ({ className, lessons, recordings, isLoading }: ProductsProps) => {
  const [openedLessons, setOpenedLessons] = useState([0]);
  const [openedRecordings, setOpenedRecordings] = useState([0]);

  const handleOpenedLessons = (index: number) => {
    openedLessons.includes(index)
      ? setOpenedLessons(openedLessons.filter(i => i !== index))
      : setOpenedLessons([...openedLessons, index]);
  };

  const handleOpenedRecordings = (index: number) => {
    openedRecordings.includes(index)
      ? setOpenedRecordings(openedRecordings.filter(i => i !== index))
      : setOpenedRecordings([...openedRecordings, index]);
  };

  return (
    <>
      <Stack sx={{ width: { xs: '100%', md: '75%' } }}>
        <Typography variant='subtitle5'>Upcoming live lessons</Typography>
        <Typography variant='body2'>
          Ensure a flawless virtual lesson experience by following these critical device readiness
          checks to prevent technical difficulties, audio issues, or video latency.
        </Typography>
      </Stack>

      <Stack sx={{ my: 3, gap: 5 }}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            {lessons.map((l, i) => (
              <Accordion
                expanded={openedLessons.includes(i)}
                onChange={() => {}}
                disableGutters
                elevation={0}
                key={i}
                square
                sx={{
                  borderBottom: openedLessons.includes(i)
                    ? ''
                    : `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                  borderTop: `solid 0px transparent`,
                  bgcolor: 'transparent',
                  '& .MuiButtonBase-root': {
                    px: 0,
                  },
                  '&::before': {
                    display: 'none',
                  },
                }}
              >
                <AccordionSummary
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                  onClick={() => handleOpenedLessons(i)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant='subtitle5'>{l.month}</Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: globalColours['$global-colour-interactive-primary-defaultå'],
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant='body3'>
                        {openedLessons.includes(i) ? 'Less' : 'More'}
                      </Typography>

                      <ArrowLeftIcon
                        sx={{
                          width: '16px',
                          height: '16px',
                          transform: openedLessons.includes(i) ? 'rotate(90deg)' : 'rotate(-90deg)',
                        }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    my: 1.5,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                    px: 0,
                    py: 0,
                  }}
                >
                  {l.lessons.map((lesson, i) => (
                    <Fragment key={i}>
                      <Appointment
                        appointmentDay={lesson.appointmentDay}
                        appointmentDate={lesson.appointmentDate}
                        appointmentTime={lesson.appointmentTime}
                        appointmentLocation={lesson.appointmentLocation}
                        appointmentInfo={lesson.appointmentInfo}
                        isDisabled={lesson.isDisabled}
                        clickAction={lesson.clickAction}
                        appointmentAvatars={[
                          'https://static03.classity.co/img/Placeholder-Avatar.png',
                          'https://static03.classity.co/img/Placeholder-Avatar.png',
                          'https://static03.classity.co/img/Placeholder-Avatar.png',
                        ]}
                      />
                    </Fragment>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
      </Stack>

      <Stack sx={{ width: '75%', mt: 5 }}>
        <Typography variant='subtitle5'>Past recorded lessons</Typography>
        <Typography variant='body2'>
          Ensure a flawless virtual lesson experience by following these critical device readiness
          checks to prevent technical difficulties, audio issues, or video latency. latency.
        </Typography>
      </Stack>

      <Stack sx={{ my: 3, gap: 5 }}>
        {recordings.map((r, i) => (
          <Accordion
            expanded={openedRecordings.includes(i)}
            onChange={() => {}}
            disableGutters
            elevation={0}
            key={i}
            square
            sx={{
              borderBottom: openedRecordings.includes(i)
                ? ''
                : `solid 1px ${globalColours['$global-colour-border-neutral']}`,
              borderTop: `solid 0px transparent`,
              bgcolor: 'transparent',
              '& .MuiButtonBase-root': {
                px: 0,
              },
              '&::before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
              onClick={() => handleOpenedRecordings(i)}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  mt: 2.5,
                }}
              >
                <Typography variant='subtitle5'>{r.date}</Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: globalColours['$global-colour-interactive-primary-defaultå'],
                    cursor: 'pointer',
                  }}
                >
                  <Typography variant='body3'>
                    {openedRecordings.includes(i) ? 'Less' : 'More'}
                  </Typography>

                  <ArrowLeftIcon
                    sx={{
                      width: '16px',
                      height: '16px',
                      transform: openedRecordings.includes(i) ? 'rotate(90deg)' : 'rotate(-90deg)',
                    }}
                  />
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                my: 1.5,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 3,
                px: 0,
                py: 0,
              }}
            >
              {Array(3)
                .fill('')
                .map((_, i) => (
                  <Box key={i}>
                    <Box
                      sx={{
                        aspectRatio: '16/9',
                        borderRadius: '8px',
                        width: 'full',
                        background: globalColours['$global-colour-palette-gray'],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PlayIcon sx={{ width: '40px', height: '40px' }} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            py: 1,
                            px: 0.75,
                          }}
                        >
                          <LikeIcon sx={{ width: '16px', height: '16px' }} />
                          <Typography variant='body3'>15</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            py: 1,
                            px: 0.75,
                          }}
                        >
                          <ChatIcon sx={{ width: '16px', height: '16px' }} />
                          <Typography variant='body3'>4</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            py: 1,
                            px: 0.75,
                          }}
                        >
                          <CycleIcon sx={{ width: '16px', height: '16px' }} />
                          <Typography variant='body3'>2</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            py: 1,
                            px: 0.75,
                          }}
                        >
                          <LinkIcon sx={{ width: '16px', height: '16px' }} />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          py: 1,
                          px: 0.75,
                        }}
                      >
                        <Typography variant='body3'>2d ago</Typography>
                      </Box>
                    </Box>
                    <Typography variant='body3'>
                      Are the eigenvalues of 𝐴=𝑃𝐷𝑃−1 the diagonal entries of 𝐷 and eigenvectors the
                      columns of 𝑃?
                    </Typography>

                    <Typography variant='body3SemiBold' sx={{ mt: 1, display: 'block' }}>
                      @eigenvectors
                    </Typography>
                  </Box>
                ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </>
  );
};

Products.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Products.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Products)(
  ({ theme }) => `
`,
);
